import { useQueries } from '@tanstack/react-query'
import type { ApiResponse, ComprehensiveScorecardData, ColumnCount, Timeframe } from '@/types'

const TIMEFRAMES: Timeframe[] = ['weekly', 'monthly', 'quarterly', 'biannual', 'yearly']

// Fetch scorecard data for each timeframe with appropriate period columns
export function useTimeframeScorecards(columns: ColumnCount = 8, enabled: boolean = true) {
  const queries = useQueries({
    queries: TIMEFRAMES.map((timeframe) => ({
      queryKey: ['scorecard', 'comprehensive', columns, 'period', timeframe],
      queryFn: async (): Promise<{ timeframe: Timeframe; data: ComprehensiveScorecardData }> => {
        const params = new URLSearchParams({
          columns: String(columns),
          label_type: 'period',
          period_timeframe: timeframe,
        })
        const res = await fetch(`/api/scorecard/comprehensive?${params}`)
        const data: ApiResponse<ComprehensiveScorecardData> = await res.json()
        if (!data.success) throw new Error(data.error || 'Failed to fetch scorecard')
        // Filter to only metrics matching this timeframe
        const filteredMetrics = data.data!.metrics.filter(m => m.timeframe === timeframe)
        return {
          timeframe,
          data: {
            periods: data.data!.periods,
            metrics: filteredMetrics,
          },
        }
      },
      enabled,
      staleTime: 30000,
    })),
  })

  const isLoading = queries.some(q => q.isLoading)
  const error = queries.find(q => q.error)?.error as Error | undefined

  // Combine results into a map by timeframe
  const dataByTimeframe = new Map<Timeframe, ComprehensiveScorecardData>()
  queries.forEach(q => {
    if (q.data && q.data.data.metrics.length > 0) {
      dataByTimeframe.set(q.data.timeframe, q.data.data)
    }
  })

  return { dataByTimeframe, isLoading, error }
}
